/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {IndexedObject, ModelResponse, ResponseData} from "@dvrd/dvr-controls";
import {ProcessingStatus, ProcessType} from "../util/interfaces";
import {
    addMissingBolProducts,
    deleteProcessStatus,
    getProcessStatus,
    getProcessStatusByType,
    updateProcessStatus
} from "../util/requestSender";

interface Props extends BaseProps {
    status?: ProcessingStatus;
    process_type?: ProcessType;
    delete_on_finish?: boolean;
    account_id?: string;
    data?: IndexedObject<any>;
}

interface Fields extends BaseFields {
    status: ProcessingStatus;
    process_type: ProcessType;
    delete_on_finish: boolean;
    account_id: string;
    data: IndexedObject<any>;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    status: ProcessingStatus.RUNNING,
    process_type: ProcessType.VAT_DECLARATION,
    delete_on_finish: true,
    account_id: '',
    data: {},
};

const fieldsDefinition: { [key in keyof Fields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    delete_on_finish: createFieldDef(ModelFieldType.BOOLEAN),
    account_id: createFieldDef(ModelFieldType.STRING),
    data: createFieldDef(ModelFieldType.OBJECT),
    process_type: createFieldDef(ModelFieldType.STRING, ProcessType.VAT_DECLARATION),
    status: createFieldDef(ModelFieldType.STRING, ProcessingStatus.RUNNING),
};

const ignoreProps: Array<keyof Props> = [];

export default class ProcessStatus extends BaseModel<Props, Fields> {
    static get = (accountId: string, id: string, callback: ModelResponse<ProcessStatus | null>) => {
        getProcessStatus({
            account_id: accountId, process_id: id, callback: (data: ResponseData) => {
                const process = data.success ? new ProcessStatus(data.process_status) : null;
                callback(process, data.success, data);
            }
        })
    };

    static getByType = (accountId: string, processType: ProcessType, callback: ModelResponse<ProcessStatus | null>) => {
        getProcessStatusByType({
            process_type: processType, account_id: accountId, callback: (data: ResponseData) => {
                const process = data.success ? new ProcessStatus(data.process_status) : null;
                callback(process, data.success, data);
            }
        })
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get status(): ProcessingStatus {
        return this.fields.status;
    }

    set status(status: ProcessingStatus) {
        this.fields.status = status;
    }

    get translatedStatus(): string {
        return {
            [ProcessingStatus.RUNNING]: 'Bezig',
            [ProcessingStatus.ERROR]: 'Fout',
            [ProcessingStatus.FINISHED]: 'Afgerond',
            [ProcessingStatus.WAITING]: 'Wachten',
        }[this.status];
    }

    get processType(): ProcessType {
        return this.fields.process_type;
    }

    set processType(process_type: ProcessType) {
        this.fields.process_type = process_type;
    }

    get deleteOnFinish(): boolean {
        return this.fields.delete_on_finish;
    }

    set deleteOnFinish(delete_on_finish: boolean) {
        this.fields.delete_on_finish = delete_on_finish;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get data(): IndexedObject<any> {
        return this.fields.data;
    }

    set data(data: IndexedObject<any>) {
        this.fields.data = data;
    }

    get progress(): string | null {
        if (!('progress' in this.data)) return null;
        return this.data.progress;
    }

    getData = (key: string): any => {
        if (this.data.hasOwnProperty(key))
            return this.data[key];
        return undefined;
    }

    save = (callback?: ModelResponse<ProcessStatus>) => {

    };

    update = (callback: ModelResponse<ProcessStatus>) => {
        updateProcessStatus({
            data: {
                account_id: this.accountId, process_id: this.id, flex: this.data,
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.process_status);
                callback(this, data.success, data);
            }
        })
    };

    delete = (callback: ModelResponse<ProcessStatus>) => {
        deleteProcessStatus({
            account_id: this.accountId, process_id: this.id, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    addMissingProducts = (callback: ModelResponse<ProcessStatus>) => {
        addMissingBolProducts({
            process_id: this.id, account_id: this.accountId, deleteAfter: true, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    }

    isActive = (): boolean => {
        return this.status === ProcessingStatus.RUNNING;
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            // TODO
        }
    };
}